// Typed API hooks. Contracts mirror backend/app/schemas.py.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./client";

// ---- shared types --------------------------------------------------------

export type Geo = { mode: "all" | "custom"; regions: string[] };

export type CampaignSummary = {
  id: string;
  is_current: boolean;
  product: string;
  product_desc: string;
  type: "seeding" | "native";
  status: string;
  platforms: string[];
  budget: number;
  spent: number;
  reach: number;
  cpv: number;
  period: string;
  creatives_count: number;
  placements_count: number;
  closed_count: number;
};

export type CampaignDetail = CampaignSummary & {
  product_url: string | null;
  product_category: string | null;
  product_type: string | null;
  geo: Geo;
  selected_blogger_ids: string[];
  creative_channel_map: Record<string, string>;
  creative_payload: Record<string, unknown>;
  payment_method: string | null;
  advertiser_data: Record<string, unknown> | null;
  wizard_step: string | null;
  wizard_draft: Record<string, unknown> | null;
};

export type Blogger = {
  id: string; name: string; full: string; platform: string;
  reach: number; price: number; cpv: number; er: number;
  score: number; cats: string[]; grad: string[]; tag: string | null;
  description?: string | null;
};

export type Forecast = {
  budget: number; bloggers: number; reach: number; impressions: number;
  frequency: number; cpv: number; cpm: number; clicks: number; cpc: number;
  leads: number; cpl: number;
};

export type BudgetConfig = {
  presets: { value: number; label: string; meta: string; flag: string | null; flag_color: string | null }[];
  tiers: { from: number; title: string; highlight: boolean; perks: string[] }[];
  benchmarks: {
    axis_max: number;
    rows: { id: string; label: string; cpv: number; kind: string; note: string }[];
    reco: string;
  };
  metric_tips: Record<string, string>;
  min: number; max: number; step: number;
};

export type GeoRegion = { id: string; label: string; share: number };
export type Platform  = { id: string; label: string; color: string; count: number };
export type Category  = { id: string; label: string; group?: string };

// ---- queries -------------------------------------------------------------

export const useCampaigns = () => useQuery({
  queryKey: ["campaigns"],
  queryFn: () => api<CampaignSummary[]>("/api/v1/campaigns"),
});

export const useCampaign = (id: string | null) => useQuery({
  queryKey: ["campaign", id],
  queryFn: () => api<CampaignDetail>(`/api/v1/campaigns/${id}`),
  enabled: !!id,
});

export const useBudgetConfig = () => useQuery({
  queryKey: ["budget", "config"],
  queryFn: () => api<BudgetConfig>("/api/v1/budget/config"),
  staleTime: Infinity,
});

export const useForecast = (budget: number) => useQuery({
  queryKey: ["forecast", budget],
  queryFn: () => api<Forecast>("/api/v1/budget/forecast", {
    method: "POST", body: JSON.stringify({ budget }),
  }),
  staleTime: 5 * 60 * 1000,
});

export const useGeoRegions = () => useQuery({
  queryKey: ["catalog", "geo"],
  queryFn: () => api<GeoRegion[]>("/api/v1/catalog/geo"),
  staleTime: Infinity,
});

export const usePlatforms = () => useQuery({
  queryKey: ["catalog", "platforms"],
  queryFn: () => api<Platform[]>("/api/v1/catalog/platforms"),
  staleTime: Infinity,
});

export const useFormatPlatforms = () => useQuery({
  queryKey: ["catalog", "format-platforms"],
  queryFn: () => api<Record<"seeding"|"native", string[]>>("/api/v1/catalog/format-platforms"),
  staleTime: Infinity,
});

export const useCategories = () => useQuery({
  queryKey: ["catalog", "categories"],
  queryFn: () => api<Category[]>("/api/v1/catalog/categories"),
  staleTime: Infinity,
});

// ---- mutations -----------------------------------------------------------

export const useBloggers = (params: {
  format?: "seeding" | "native"; platforms?: string[]; search?: string; sort?: "cpv" | "reach" | "price";
}) => useQuery({
  queryKey: ["bloggers", params],
  queryFn: () => {
    const u = new URLSearchParams();
    if (params.format)  u.set("format", params.format);
    if (params.sort)    u.set("sort", params.sort);
    if (params.search)  u.set("search", params.search);
    (params.platforms ?? []).forEach((p) => u.append("platform", p));
    return api<Blogger[]>(`/api/v1/bloggers?${u.toString()}`);
  },
});

export const useRecommended = (format?: "seeding" | "native") => useQuery({
  queryKey: ["bloggers", "recommended", format],
  queryFn: () => api<{ blogger: Blogger; reason: string; reason_type: string }[]>(
    `/api/v1/bloggers/recommended${format ? `?format=${format}` : ""}`),
});

export const useAdjacentBloggers = (format?: "seeding" | "native", category?: string) => useQuery({
  queryKey: ["bloggers", "adjacent", format, category],
  queryFn: () => {
    const u = new URLSearchParams();
    if (format) u.set("format", format);
    if (category) u.set("category", category);
    const qs = u.toString();
    return api<{ blogger: Blogger; reason: string; matched_niche: string }[]>(
      `/api/v1/bloggers/adjacent${qs ? `?${qs}` : ""}`
    );
  },
});

export const useCatalogTags = () => useQuery({
  queryKey: ["catalog", "tags"],
  queryFn: () => api<string[]>("/api/v1/catalog/tags"),
  staleTime: Infinity,
});

export const useAdvFilters = () => useQuery({
  queryKey: ["catalog", "adv-filters"],
  queryFn: () => api<{ id: string; label: string; tip: string | null; opts: string[]; kind?: "range" | "chips"; unit?: string | null }[]>("/api/v1/catalog/adv-filters"),
  staleTime: Infinity,
});

export type Placement = {
  id: string; campaign_id: string; blogger_id: string; creative_id: string | null;
  status: string; publish_date: string; price: number; msg: string;
  result: { views: number; clicks: number; ctr: number; cpv: number; conv: number } | null;
  top_performer: boolean; decline_reason: string | null;
  replacements: string[] | null; is_new: boolean;
  // UTM fields
  target_url: string | null;
  placement_type: string | null;
  utm_link: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  utm_is_custom: boolean;
  utm_generated_at: string | null;
  // Blogger-side report + payout
  report_link: string | null;
  report_screenshot_url: string | null;
  report_submitted_at: string | null;
  paid_at: string | null;
  // Native-only script approval loop (unused for seeding)
  script_draft: string | null;
  script_status: "pending" | "approved" | "changes_requested" | null;
  deadline_at: string | null;
  is_overdue: boolean;
  // Advertiser-initiated cancellation
  cancel_reason: string | null;
  cancelled_at: string | null;
};

export type UtmPreview = {
  utm_link: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string | null;
  has_existing_utm: boolean;
};

export type PlacementPatch = {
  target_url?: string | null;
  placement_type?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  utm_is_custom?: boolean;
};

export type KanbanResponse = {
  columns: { id: string; label: string }[];
  placements: Placement[];
  campaign: CampaignDetail;
  metrics: { spent: number; budget: number; live_views: number; live_clicks: number };
  scale_banner: { placement_id: string; blogger_id: string; cpv: number; avg_cpv: number; times_better: number } | null;
};

export const useKanban = (campaignId: string | null) => useQuery({
  queryKey: ["kanban", campaignId],
  queryFn: () => api<KanbanResponse>(`/api/v1/campaigns/${campaignId}/kanban`),
  enabled: !!campaignId,
});

export type ChatThread = {
  blogger_id: string; last: string; time: string; unread: number;
  messages: { from_who: "me" | "them"; text: string; time_label: string }[];
};

export const useChats = (campaignId: string | null) => useQuery({
  queryKey: ["chats", campaignId],
  queryFn: () => api<{ threads: ChatThread[]; quick_replies: string[] }>(`/api/v1/campaigns/${campaignId}/chats`),
  enabled: !!campaignId,
});

export function useSendMessage(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { blogger_id: string; text: string }) =>
      api<{ ok: boolean; id: number }>(`/api/v1/campaigns/${campaignId}/chats/messages`, {
        method: "POST", body: JSON.stringify(body),
      }),
    onMutate: async (body) => {
      // Optimistically append the message so the bubble appears instantly.
      const key = ["chats", campaignId];
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<{ threads: ChatThread[]; quick_replies: string[] }>(key);
      if (prev) {
        const optimistic = {
          ...prev,
          threads: prev.threads.map((t) =>
            t.blogger_id === body.blogger_id
              ? {
                  ...t,
                  last: body.text,
                  time: "сейчас",
                  messages: [...t.messages, { from_who: "me" as const, text: body.text, time_label: "сейчас" }],
                }
              : t),
        };
        qc.setQueryData(key, optimistic);
      }
      qc.invalidateQueries({ queryKey: ["inbox"] });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["chats", campaignId], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["chats", campaignId] });
      qc.invalidateQueries({ queryKey: ["inbox"] });
    },
  });
}

export type AnalyticsOverview = {
  kpis: { key: string; label: string; value: number; delta_pct: number; good_is?: string; format: "num" | "cpv" | "rub"; tip: string }[];
  bars: { id: string; label: string; value: number; type: string }[];
  socials: { platform: string; views: number; cpv: number | null; share_pct: number }[];
  social_insight: string | null;
  best_channels: { blogger_id: string; name: string; platform: string; views: number; cpv: number; conv: number }[];
};

export const useAnalytics = (campaignIds: string[] = []) => useQuery({
  queryKey: ["analytics", "overview", campaignIds],
  queryFn: () => {
    const u = new URLSearchParams();
    campaignIds.forEach((id) => u.append("campaign_id", id));
    const qs = u.toString();
    return api<AnalyticsOverview>(`/api/v1/analytics/overview${qs ? `?${qs}` : ""}`);
  },
});

export type DocumentItem = {
  id: number; campaign_id: string | null; kind: string; title: string;
  status: string; amount_rub: number | null; erid: string | null;
};

export const useDocuments = () => useQuery({
  queryKey: ["documents"],
  queryFn: () => api<DocumentItem[]>("/api/v1/documents"),
});

export type InboxThread = {
  campaign_id: string;
  campaign: { id: string; product: string; type: "seeding" | "native"; status: string };
  blogger_id: string;
  last: string;
  time: string;
  unread: number;
  messages: { from_who: "me" | "them"; text: string; time_label: string }[];
};

export type InboxResponse = {
  threads: InboxThread[];
  campaigns: { id: string; product: string; type: "seeding" | "native"; status: string }[];
  quick_replies: string[];
  unread_threads_count: number;
};

export const useInbox = () => useQuery({
  queryKey: ["inbox"],
  queryFn: () => api<InboxResponse>("/api/v1/inbox"),
});

export function useMarkThreadRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, bloggerId }: { campaignId: string; bloggerId: string }) =>
      api<{ ok: boolean; marked: number }>(
        `/api/v1/inbox/threads/${campaignId}/${bloggerId}/mark-read`,
        { method: "POST", body: "{}" },
      ),
    onMutate: ({ campaignId, bloggerId }) => {
      // Optimistic: drop the badge immediately so the click feels snappy.
      const prev = qc.getQueryData<InboxResponse>(["inbox"]);
      if (prev) {
        const next = {
          ...prev,
          threads: prev.threads.map((t) =>
            t.campaign_id === campaignId && t.blogger_id === bloggerId ? { ...t, unread: 0 } : t,
          ),
        };
        next.unread_threads_count = next.threads.filter((t) => t.unread > 0).length;
        qc.setQueryData(["inbox"], next);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["inbox"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["inbox"] }),
  });
}

/** Every mutation that touches a Placement has to invalidate on both sides
 * of the role switcher, not just its own query key — otherwise the
 * advertiser's kanban, the blogger's inbox/payouts, the blogger picker list
 * ("mine") and the shared chat inbox badge can each go stale independently
 * within the same tab/session. Broad prefix invalidation (React Query
 * matches by key-prefix) is deliberate here over tracking exact bloggerIds:
 * replace() e.g. touches two different bloggers (the one being replaced and
 * the new one) and the call site doesn't always know both. */
function invalidatePlacementSideEffects(qc: ReturnType<typeof useQueryClient>, campaignId?: string) {
  if (campaignId) qc.invalidateQueries({ queryKey: ["kanban", campaignId] });
  qc.invalidateQueries({ queryKey: ["blogger-portal"] });
  qc.invalidateQueries({ queryKey: ["inbox"] });
}

export function useScalePlacement(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (placementId: string) =>
      api<{ ok: boolean; placement_id: string }>(
        `/api/v1/campaigns/${campaignId}/placements/${placementId}/scale`,
        { method: "POST", body: "{}" }),
    onSuccess: () => invalidatePlacementSideEffects(qc, campaignId),
  });
}

export function useReplacePlacement(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ placementId, newBloggerId }: { placementId: string; newBloggerId: string }) =>
      api<{ ok: boolean; placement_id: string }>(
        `/api/v1/campaigns/${campaignId}/placements/${placementId}/replace`,
        { method: "POST", body: JSON.stringify({ new_blogger_id: newBloggerId }) }),
    onSuccess: () => invalidatePlacementSideEffects(qc, campaignId),
  });
}

export function useCancelPlacement(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ placementId, reason }: { placementId: string; reason?: string }) =>
      api<{ ok: boolean; status: string }>(
        `/api/v1/campaigns/${campaignId}/placements/${placementId}/cancel`,
        { method: "POST", body: JSON.stringify({ reason: reason ?? null }) }),
    onSuccess: () => invalidatePlacementSideEffects(qc, campaignId),
  });
}

export function useApproveScript(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (placementId: string) =>
      api<{ ok: boolean; status: string }>(
        `/api/v1/campaigns/${campaignId}/placements/${placementId}/approve-script`,
        { method: "POST", body: "{}" }),
    onSuccess: () => invalidatePlacementSideEffects(qc, campaignId),
  });
}

export function useRequestScriptChanges(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ placementId, message }: { placementId: string; message: string }) =>
      api<{ ok: boolean; status: string }>(
        `/api/v1/campaigns/${campaignId}/placements/${placementId}/request-script-changes`,
        { method: "POST", body: JSON.stringify({ message }) }),
    onSuccess: () => invalidatePlacementSideEffects(qc, campaignId),
  });
}

export type BloggerExpandConfig = {
  blogger_id: string;
  placement_type?: string;
  format_id?: string;
  format_name?: string;
  utm_content?: string;
  variant_id?: string;
  date?: string;
  date_to?: string;
};

export function useExpandCampaign(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: string[] | { blogger_ids: string[]; configs: BloggerExpandConfig[] }) => {
      const body = Array.isArray(payload)
        ? { blogger_ids: payload }
        : payload;
      return api<{ ok: boolean; added: number }>(
        `/api/v1/campaigns/${campaignId}/expand`,
        { method: "POST", body: JSON.stringify(body) });
    },
    onSuccess: () => invalidatePlacementSideEffects(qc, campaignId),
  });
}

/** Same /expand call as useExpandCampaign, but for call sites (e.g. the
 * catalog) that don't know which campaign they're targeting until the user
 * picks one at confirm-time — campaignId is a mutate-time variable instead
 * of a hook argument. */
export function useAddBloggerToCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, config }: { campaignId: string; config: BloggerExpandConfig }) =>
      api<{ ok: boolean; added: number }>(
        `/api/v1/campaigns/${campaignId}/expand`,
        { method: "POST", body: JSON.stringify({ blogger_ids: [config.blogger_id], configs: [config] }) }),
    onSuccess: (_data, vars) => invalidatePlacementSideEffects(qc, vars.campaignId),
  });
}

export function useUtmPreview(campaignId: string, placementId: string) {
  return useMutation({
    mutationFn: ({ targetUrl, placementType }: { targetUrl: string; placementType: string }) => {
      const u = new URLSearchParams({ target_url: targetUrl, placement_type: placementType });
      return api<UtmPreview>(
        `/api/v1/campaigns/${campaignId}/placements/${placementId}/utm-preview?${u.toString()}`,
      );
    },
  });
}

export function usePatchPlacement(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ placementId, patch }: { placementId: string; patch: PlacementPatch }) =>
      api<Placement>(`/api/v1/campaigns/${campaignId}/placements/${placementId}`, {
        method: "PATCH", body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kanban", campaignId] }),
  });
}

// ---- media -----------------------------------------------------------

export type MediaItem = {
  id: string; kind: "image" | "video" | "emoji"; url: string;
  label: string | null; mime: string; size_bytes: number;
};

export function useUploadMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { file: File; kind?: "image" | "video" | "emoji"; label?: string }) => {
      const fd = new FormData();
      fd.append("file", params.file);
      fd.append("kind", params.kind ?? "image");
      if (params.label) fd.append("label", params.label);
      const res = await fetch("/api/v1/media/upload", { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) {
        let body: unknown = undefined;
        try { body = await res.json(); } catch {}
        throw new ApiError(res.status, `Upload ${res.status}`, body);
      }
      return res.json() as Promise<MediaItem>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media"] }),
  });
}

export const useMyMedia = (kind?: "image" | "video" | "emoji") => useQuery({
  queryKey: ["media", kind ?? "all"],
  queryFn: () => api<MediaItem[]>(`/api/v1/media${kind ? `?kind=${kind}` : ""}`),
});

export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/api/v1/media/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media"] }),
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { type: "seeding" | "native"; product_url?: string }) =>
      api<CampaignDetail>("/api/v1/campaigns", {
        method: "POST", body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}

export type DraftSavePayload = {
  campaign_id?: string | null;
  wizard_step: string;
  wizard_draft: Record<string, unknown>;
  product?: string;
  product_desc?: string;
  product_url?: string;
  product_category?: string;
  product_type?: string;
  type?: "seeding" | "native";
  budget?: number;
  geo?: Geo;
  selected_blogger_ids?: string[];
  platforms?: string[];
};

// ---- collections ("Подборки") ---------------------------------------------

export type CollectionSummary = {
  id: string;
  name: string;
  is_active: boolean;
  target_campaign_id: string | null;
  members_count: number;
  platforms: string[];
  total_reach: number;
  base_total_price: number;
};

export type AiBriefInput = {
  url: string;
  budget: number;
  what: string;
  geo: string[];
};

export type AiBrief = {
  site_understanding: string;
  brief_understanding: string;
  icp: string;
  budget_analysis: string;
  site_fetched: boolean;
  input: AiBriefInput;
};

export type CollectionDetail = CollectionSummary & {
  members: { blogger_id: string; note: string | null }[];
  ai_brief: AiBrief | null;
};

export type Membership = Record<string, { id: string; name: string }[]>;

export const useCollections = () => useQuery({
  queryKey: ["collections"],
  queryFn: () => api<CollectionSummary[]>("/api/v1/collections"),
});

export const useCollection = (id: string | null) => useQuery({
  queryKey: ["collection", id],
  queryFn: () => api<CollectionDetail>(`/api/v1/collections/${id}`),
  enabled: !!id,
});

export const useMembership = () => useQuery({
  queryKey: ["collections", "membership"],
  queryFn: () => api<Membership>("/api/v1/collections/me/membership"),
});

export function useCreateCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; target_campaign_id?: string | null }) =>
      api<CollectionDetail>("/api/v1/collections", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collections"] });
      qc.invalidateQueries({ queryKey: ["collections", "membership"] });
    },
  });
}

export function usePatchCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { name?: string; is_active?: boolean; target_campaign_id?: string | null } }) =>
      api<CollectionDetail>(`/api/v1/collections/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["collections"] });
      qc.invalidateQueries({ queryKey: ["collection", v.id] });
    },
  });
}

export function useDeleteCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/v1/collections/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collections"] });
      qc.invalidateQueries({ queryKey: ["collections", "membership"] });
    },
  });
}

export function useAddBloggersToCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, blogger_ids }: { id: string; blogger_ids: string[] }) =>
      api<CollectionDetail>(`/api/v1/collections/${id}/bloggers`, {
        method: "POST", body: JSON.stringify({ blogger_ids }),
      }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["collections"] });
      qc.invalidateQueries({ queryKey: ["collection", v.id] });
      qc.invalidateQueries({ queryKey: ["collections", "membership"] });
    },
  });
}

export function useRemoveBloggerFromCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, blogger_id }: { id: string; blogger_id: string }) =>
      api<CollectionDetail>(`/api/v1/collections/${id}/bloggers/${blogger_id}`, { method: "DELETE" }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["collections"] });
      qc.invalidateQueries({ queryKey: ["collection", v.id] });
      qc.invalidateQueries({ queryKey: ["collections", "membership"] });
    },
  });
}

export function useLaunchCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ campaign_id: string; wizard_step: string }>(
        `/api/v1/collections/${id}/launch`, { method: "POST", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}

export function useExtendCampaignFromCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, campaignId, bloggerIds }: { collectionId: string; campaignId: string; bloggerIds?: string[] }) =>
      api<{ ok: boolean; added: number; skipped_already_in_campaign: number; skipped_missing: number; total_members: number }>(
        `/api/v1/collections/${collectionId}/extend`,
        { method: "POST", body: JSON.stringify({ campaign_id: campaignId, ...(bloggerIds !== undefined ? { blogger_ids: bloggerIds } : {}) }) },
      ),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["kanban", v.campaignId] });
    },
  });
}

export async function exportCollectionXlsx(id: string): Promise<void> {
  const res = await fetch(`/api/v1/collections/${id}/export`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token") ?? ""}` },
  });
  if (!res.ok) throw new Error("Ошибка экспорта");
  const blob = await res.blob();
  const disp = res.headers.get("Content-Disposition") ?? "";
  const match = disp.match(/filename\*=UTF-8''(.+)/i);
  const filename = match ? decodeURIComponent(match[1]) : "подборка.xlsx";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ---- Creative rewrite (wizard step 04) -------------------------------------

export type CreativeRewriteRequest = {
  tone: string;
  product_name: string;
  product_desc?: string;
  product_url?: string;
  product_category?: string;
  current_text?: string;
};

export function useCreativeRewrite() {
  return useMutation({
    mutationFn: (body: CreativeRewriteRequest) =>
      api<{ text: string }>("/api/v1/creative/rewrite", {
        method: "POST", body: JSON.stringify(body),
      }),
  });
}

// ---- Product autofill (wizard step 01) -------------------------------------

export type ProductParseResult = {
  product_name: string;
  product_category: string;
  product_type: string;
  product_desc: string;
  site_fetched: boolean;
  source: "site" | "websearch" | "unknown";
};

export function useProductParseUrl() {
  return useMutation({
    mutationFn: (url: string) =>
      api<ProductParseResult>("/api/v1/product/parse-url", {
        method: "POST", body: JSON.stringify({ url }),
      }),
  });
}

// ---- Help / AI-collection --------------------------------------------------

export type AiCollectionRequest = {
  url: string;
  budget: number;
  what: string;
  geo: string[];
};

export type AiCollectionResponse = {
  collection_id: string;
  name: string;
  brand_summary: string;
  picks: { blogger_id: string; reason: string }[];
  site_fetched: boolean;
  brief: AiBrief;
};

export function useCreateAiCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AiCollectionRequest) =>
      api<AiCollectionResponse>("/api/v1/help/ai-collection", {
        method: "POST", body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collections"] });
      qc.invalidateQueries({ queryKey: ["collections", "membership"] });
    },
  });
}

// ---- per-campaign creatives ------------------------------------------------

export type VariantStatus = "active" | "draft" | "archived";

export type CampaignCreativeVariant = {
  id: string; name: string; tone: string; text: string; link: string;
  media: { id: string; kind: "image" | "video"; url: string }[];
  status?: VariantStatus;
};

export type AllCreativeItem = {
  campaign_id: string;
  campaign_product: string;
  campaign_type: "seeding" | "native";
  campaign_status: "draft" | "active" | "moderation" | "done";
  variant: CampaignCreativeVariant & { status: VariantStatus };
};

export type CreativeStatEntry = {
  views: number; clicks: number; conv: number;
  ctr: number; cpv: number; placements: number;
};

export const useAllCreatives = () => useQuery({
  queryKey: ["all-creatives"],
  queryFn: () => api<AllCreativeItem[]>("/api/v1/creatives"),
});

export const useCreativeStats = () => useQuery({
  queryKey: ["creative-stats"],
  queryFn: () => api<Record<string, CreativeStatEntry>>("/api/v1/creatives/stats"),
});

export function usePatchCreativeStatus(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ variantId, status }: { variantId: string; status: VariantStatus }) =>
      api<{ ok: boolean }>(`/api/v1/creatives/${campaignId}/variants/${variantId}`, {
        method: "PATCH", body: JSON.stringify({ status }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all-creatives"] });
      qc.invalidateQueries({ queryKey: ["creative-stats"] });
    },
  });
}

export const useCampaignCreatives = (campaignId: string | null) => useQuery({
  queryKey: ["campaign-creatives", campaignId],
  queryFn: () => api<{ variants: CampaignCreativeVariant[] }>(`/api/v1/campaigns/${campaignId}/creatives`),
  enabled: !!campaignId,
});

export function useReplaceCampaignCreatives(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variants: CampaignCreativeVariant[]) =>
      api<{ variants: CampaignCreativeVariant[]; creatives_count: number }>(
        `/api/v1/campaigns/${campaignId}/creatives`,
        { method: "PUT", body: JSON.stringify({ variants }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-creatives", campaignId] });
      qc.invalidateQueries({ queryKey: ["kanban", campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["all-creatives"] });
    },
  });
}

export function useSaveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DraftSavePayload) =>
      api<CampaignDetail>("/api/v1/campaigns/draft", {
        method: "POST", body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["campaign", data.id] });
    },
  });
}

export function usePatchCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CampaignDetail> }) =>
      api<CampaignDetail>(`/api/v1/campaigns/${id}`, {
        method: "PATCH", body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["campaign", vars.id] });
    },
  });
}

export function useLaunchCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, method, utmPerBlogger, placementDates }: {
      id: string; method: "card" | "invoice";
      utmPerBlogger?: Record<string, string>;
      placementDates?: Record<string, { date: string; date_to: string }>;
    }) =>
      api<{ ok: boolean; campaign_id: string; placements: number }>(
        `/api/v1/campaigns/${id}/launch`, {
          method: "POST",
          body: JSON.stringify({
            method,
            utm_content_overrides: utmPerBlogger,
            placement_dates: placementDates,
          }),
        }
      ),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useSimTick() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignId?: string) =>
      api<{ ok: boolean; advanced: number }>(
        `/api/v1/sim/tick${campaignId ? `?campaign_id=${campaignId}` : ""}`,
        { method: "POST", body: "{}" }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useDemoReset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: "preset" | "blank" = "preset") =>
      api<{ ok: boolean; mode: string }>(`/api/v1/demo/reset?mode=${mode}`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

// ---- Blogger cabinet -------------------------------------------------------
// Same session as the advertiser side (see backend/app/api/v1/blogger_portal.py
// docstring) — these hooks just view/mutate the advertiser's own Placement
// rows filtered by blogger_id instead of campaign_id.

export type BloggerWithPlacements = Blogger & { placements_count: number; new_count: number };

export type BloggerPlacement = Placement & {
  campaign_product: string;
  campaign_type: "seeding" | "native";
  blogger_status_label: string;
};

export type BloggerInbox = {
  blogger: Blogger;
  placements: BloggerPlacement[];
  columns: { id: string; label: string }[];
  balance_rub: number;
};

export type BloggerPayout = {
  id: number;
  blogger_id: string;
  campaign_product: string;
  amount: number;
  balance_after: number;
  created_at: string;
};

export const useBloggersWithPlacements = () => useQuery({
  queryKey: ["blogger-portal", "mine"],
  queryFn: () => api<BloggerWithPlacements[]>("/api/v1/blogger-portal/mine"),
});

export const useBloggerInbox = (bloggerId: string | null) => useQuery({
  queryKey: ["blogger-portal", "inbox", bloggerId],
  queryFn: () => api<BloggerInbox>(`/api/v1/blogger-portal/${bloggerId}/inbox`),
  enabled: !!bloggerId,
});

export const useBloggerPayouts = (bloggerId: string | null) => useQuery({
  queryKey: ["blogger-portal", "payouts", bloggerId],
  queryFn: () => api<{ balance_rub: number; payouts: BloggerPayout[] }>(`/api/v1/blogger-portal/${bloggerId}/payouts`),
  enabled: !!bloggerId,
});

function useBloggerAction<TBody = void>(action: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bloggerId, placementId, body }: { bloggerId: string; placementId: string; body?: TBody }) =>
      api<{ ok: boolean; status: string; campaign_id?: string }>(
        `/api/v1/blogger-portal/${bloggerId}/placements/${placementId}/${action}`,
        { method: "POST", body: JSON.stringify(body ?? {}) }),
    onSuccess: (data) => invalidatePlacementSideEffects(qc, data.campaign_id),
  });
}

export const useAcceptPlacement = () => useBloggerAction("accept");
export const useDeclinePlacement = () => useBloggerAction<{ reason?: string }>("decline");
export const useRequestChanges = () => useBloggerAction<{ message: string }>("request-changes");

export function useSubmitScript() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bloggerId, placementId, scriptDraft }: {
      bloggerId: string; placementId: string; scriptDraft: string;
    }) =>
      api<Placement>(
        `/api/v1/blogger-portal/${bloggerId}/placements/${placementId}/submit-script`,
        { method: "POST", body: JSON.stringify({ script_draft: scriptDraft }) }),
    onSuccess: (data) => invalidatePlacementSideEffects(qc, data.campaign_id),
  });
}

export function useSubmitReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bloggerId, placementId, link, screenshotUrl }: {
      bloggerId: string; placementId: string; link: string; screenshotUrl?: string | null;
    }) =>
      api<Placement>(
        `/api/v1/blogger-portal/${bloggerId}/placements/${placementId}/submit-report`,
        { method: "POST", body: JSON.stringify({ link, screenshot_url: screenshotUrl ?? null }) }),
    onSuccess: (data) => invalidatePlacementSideEffects(qc, data.campaign_id),
  });
}

/** Advertiser side: 'review' -> 'done' + pays the blogger. */
export function useConfirmPlacement(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (placementId: string) =>
      api<{ ok: boolean; status: string; paid: number; blogger_balance: number }>(
        `/api/v1/campaigns/${campaignId}/placements/${placementId}/confirm`,
        { method: "POST", body: "{}" }),
    onSuccess: () => invalidatePlacementSideEffects(qc, campaignId),
  });
}
